import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import * as S from "./styles";
import UserAvatar from "../userAvatar";
import { useUserContext } from "../../contexts/user/userContext";
import {
  IContact,
  searchContact,
  updateContact,
} from "../../utils/services/modules/contacts";
import { getApiError } from "../../utils/services/database";
import { canShare, copyText, shareUserCode } from "../../utils/user";

interface IContactsSearch {
  onShowContacts: () => void;
}

type Status = "idle" | "loading" | "found" | "notFound" | "self" | "error";

const CODE_LENGTH = 4;

const ContactsSearch: React.FC<IContactsSearch> = ({ onShowContacts }) => {
  const navigate = useNavigate();
  const { state, dispatch } = useUserContext();
  const { user } = state;

  const [code, setCode] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [result, setResult] = useState<IContact | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [adding, setAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [copied, setCopied] = useState(false);

  // ignora respostas de buscas antigas (a pessoa já digitou outro código)
  const requestId = useRef(0);

  const isContact = (contactCode: number) =>
    !!user?.contacts?.some((contact) => contact.code === contactCode);

  const search = async (value: string) => {
    if (value.length !== CODE_LENGTH || !user) return;

    const numericCode = parseInt(value, 10);
    setResult(null);
    setJustAdded(false);

    if (numericCode === user.code) {
      setStatus("self");
      return;
    }

    const id = ++requestId.current;
    setStatus("loading");
    try {
      const found = await searchContact(numericCode, user.token);
      if (id !== requestId.current) return;
      setResult(found);
      setStatus("found");
    } catch (error: any) {
      if (id !== requestId.current) return;
      if (error?.response?.status === 404) {
        setStatus("notFound");
      } else {
        setErrorMessage(
          getApiError(error, "Não foi possível buscar agora. Tente novamente."),
        );
        setStatus("error");
      }
    }
  };

  // busca sozinha ao completar os 4 números
  const handleChange = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(digits);
    if (digits.length === CODE_LENGTH) {
      search(digits);
    } else {
      requestId.current++;
      setStatus("idle");
      setResult(null);
      setJustAdded(false);
    }
  };

  const handleAdd = async () => {
    if (!result || !user || adding) return;
    setAdding(true);
    try {
      const contacts = await updateContact(result.code, "add", user.token);
      dispatch({ type: "UPDATE_CONTACTS", payload: contacts });
      setJustAdded(true);
    } catch (error) {
      toast.error(getApiError(error, "Não foi possível adicionar o contato."));
    } finally {
      setAdding(false);
    }
  };

  const handleCopyOwnCode = async () => {
    if (!user) return;
    if (await copyText(String(user.code))) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const ready = code.length === CODE_LENGTH;

  return (
    <S.Container>
      <S.Label htmlFor="contact-code">Código do contato</S.Label>
      <S.SearchRow>
        <S.CodeInput
          id="contact-code"
          value={code}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") search(code);
          }}
          placeholder="0000"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={CODE_LENGTH}
          autoComplete="off"
          enterKeyHint="search"
          autoFocus
        />
        <S.SearchButton
          type="button"
          $inactive={!ready}
          aria-disabled={!ready}
          onClick={() => search(code)}
        >
          {status === "loading" ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <S.SearchIcon />
          )}
          Buscar
        </S.SearchButton>
      </S.SearchRow>
      <S.Helper>
        Peça o código de 4 números para a pessoa. Ele aparece no perfil dela.
      </S.Helper>

      {status === "found" && result && (
        <S.ResultCard>
          <S.ResultHeader>
            <UserAvatar
              name={result.name}
              code={result.code}
              avatar={result.avatar}
              size={52}
            />
            <S.InfoGroup>
              <S.Name>{result.name}</S.Name>
              <S.SubInfo>ID {result.code}</S.SubInfo>
            </S.InfoGroup>
          </S.ResultHeader>

          {isContact(result.code) ? (
            <>
              <S.Badge $success={justAdded}>
                <S.AddedIcon />
                {justAdded
                  ? "Adicionado aos seus contatos!"
                  : "Já está nos seus contatos"}
              </S.Badge>
              <S.ButtonRow>
                <S.PrimaryButton
                  type="button"
                  onClick={() => navigate(`/wallet?code=${result.code}`)}
                >
                  <S.SendIcon />
                  Enviar moedas
                </S.PrimaryButton>
                <S.SecondaryButton type="button" onClick={onShowContacts}>
                  Ver contatos
                </S.SecondaryButton>
              </S.ButtonRow>
            </>
          ) : (
            <S.PrimaryButton type="button" onClick={handleAdd}>
              {adding ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <>
                  <S.AddIcon />
                  Adicionar aos contatos
                </>
              )}
            </S.PrimaryButton>
          )}
        </S.ResultCard>
      )}

      {status === "notFound" && (
        <S.Message>
          <S.NotFoundIcon />
          <S.MessageTitle>Nenhum usuário com o código {code}</S.MessageTitle>
          <S.MessageText>Confira os números com a pessoa.</S.MessageText>
        </S.Message>
      )}

      {status === "self" && (
        <S.Message>
          <S.SelfIcon />
          <S.MessageTitle>Esse é o seu código</S.MessageTitle>
          <S.MessageText>
            Compartilhe com quem vai te enviar moedas ou te adicionar.
          </S.MessageText>
        </S.Message>
      )}

      {status === "error" && (
        <S.Message>
          <S.MessageText>{errorMessage}</S.MessageText>
          <S.RetryButton type="button" onClick={() => search(code)}>
            Tentar novamente
          </S.RetryButton>
        </S.Message>
      )}

      {user && (
        <S.OwnCode>
          <S.OwnCodeText>
            Seu código: <strong>{user.code}</strong>
          </S.OwnCodeText>
          <S.OwnCodeActions>
            <S.LinkButton type="button" onClick={handleCopyOwnCode}>
              {copied ? <S.CopiedIcon /> : <S.CopyIcon />}
              {copied ? "Copiado" : "Copiar"}
            </S.LinkButton>
            {canShare() && (
              <S.LinkButton
                type="button"
                onClick={() => shareUserCode(user.code)}
              >
                <S.ShareIcon />
                Compartilhar
              </S.LinkButton>
            )}
          </S.OwnCodeActions>
        </S.OwnCode>
      )}
    </S.Container>
  );
};

export default ContactsSearch;
